// ---------------------------------------------------------------------------
// A total that climbs to where it landed.
//
// The reveal shows the score a player had, a `+n` beside it, and then runs the
// number up through every point they gained. That is the whole feature, and
// three things about it are load-bearing rather than decorative:
//
//   1. It is driven off the two numbers the row already has. Nothing about the
//      count comes from the server or from a parent's state, so no screen has to
//      thread "this row is new" down to a leaf.
//   2. It restarts only when those two numbers change. The reveal re-renders
//      every time the server's cursor moves, and a count that started over on
//      each of those would have every score on the screen permanently ticking.
//      The effect's dependencies are the props, so an unrelated re-render is
//      free. A host override that really does change a score is a change to
//      those props, and it correctly counts again from the new starting point.
//   3. Reduced motion skips it rather than shortening it. The number is the
//      information; the climb is the performance. Someone who has asked for no
//      motion gets the information with none of the performance, on the first
//      paint, and `performing` stays false so whatever was explaining the delta
//      stays put instead of animating away.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { prefersReducedMotion } from '../store/settingsStore.js';

/**
 * Milliseconds per integer. Fast enough to read as a tick rather than a
 * transition, slow enough that 7 to 12 is five visible numbers and not a blur.
 */
const MS_PER_STEP = 70;

/**
 * The ceiling, past which the count interpolates instead of stepping. A round
 * that moved somebody 18 points should not hold the screen for a second and a
 * quarter, and nobody is reading the intermediate numbers by then anyway.
 */
const MAX_MS = 620;

/**
 * The beat before the climb starts.
 *
 * Without it the whole thing is invisible: the row would mount already counting,
 * and the `+5` that is supposed to explain where the extra points came from
 * would never be on screen at the old number. Long enough to read "7 +5", short
 * enough that it does not feel like the screen has stalled.
 */
const HOLD_MS = 320;

/**
 * Which of the three beats the row is on.
 *
 *   holding   the number has not moved and the badge is carrying the
 *             explanation. A caller that is not animating — anyone with reduced
 *             motion on, or a caller passing `animate: false` — sits here
 *             permanently, already on the final total, because the badge is then
 *             the only thing saying where that total came from.
 *   counting  the number is climbing and the badge is fading out, on the same
 *             clock, because the climb is now saying what the badge was saying.
 *   settled   the final total, and nothing beside it.
 */
export type CountUpPhase = 'holding' | 'counting' | 'settled';

export interface CountUpReading {
  /** The integer to draw right now. */
  value: number;
  phase: CountUpPhase;
}

function opening(from: number, to: number, climbing: boolean): CountUpReading {
  return { value: climbing ? from : to, phase: 'holding' };
}

/**
 * Counts from `from` to `to`, once, whenever either changes.
 *
 * `animate` is the caller's own reason to skip, for a total being reported
 * rather than earned: a second climb over a number somebody has already watched
 * move reads as the score changing twice. The caller that had that problem was
 * the between-rounds scoreboard, and that phase is gone — the reveal's closing
 * slide is the standing now — so every live caller animates. Reduced motion is
 * handled separately, below, and is not this flag.
 */
export function useCountUp(from: number, to: number, animate = true): CountUpReading {
  const climbing = animate && from !== to && !prefersReducedMotion();

  const [reading, setReading] = useState<CountUpReading>(() => opening(from, to, climbing));
  const [shown, setShown] = useState({ from, to, animate });

  // Reset during render rather than from an effect. The alternative paints the
  // old row's numbers for one frame before correcting itself, which on a host
  // override is the score visibly landing on the wrong value first.
  if (shown.from !== from || shown.to !== to || shown.animate !== animate) {
    setShown({ from, to, animate });
    setReading(opening(from, to, climbing));
  }

  useEffect(() => {
    if (!climbing) return;

    const distance = to - from;
    const duration = Math.min(Math.abs(distance) * MS_PER_STEP, MAX_MS);
    let frame = 0;

    const lead = setTimeout(() => {
      const started = performance.now();

      // Linear, deliberately. An eased count spends its last frames crawling
      // over the final two integers, which reads as the number struggling to
      // land rather than as a tally running up.
      //
      // The elapsed time is read from `performance.now()` inside the tick and
      // NOT from the timestamp requestAnimationFrame hands in. The two agree in
      // a browser, where the rAF timestamp is on the performance timeline, and
      // they do not have to agree anywhere else: under the jsdom this suite runs
      // on, the rAF timestamp starts from a different origin, so `now - started`
      // came out negative, the number counted *down* from 12 to 0, and
      // `progress < 1` stayed true forever so it never settled. One clock, read
      // in one place, cannot disagree with itself.
      const tick = () => {
        // Clamped at both ends. The ceiling is what ends the animation; the
        // floor means that even a clock that runs backwards leaves the number
        // sitting on the total the player actually had, rather than below it.
        const elapsed = performance.now() - started;
        const progress = Math.max(0, Math.min(1, elapsed / duration));
        setReading({
          value: from + Math.round(distance * progress),
          phase: progress < 1 ? 'counting' : 'settled',
        });
        if (progress < 1) frame = requestAnimationFrame(tick);
      };

      frame = requestAnimationFrame(tick);
    }, HOLD_MS);

    return () => {
      clearTimeout(lead);
      cancelAnimationFrame(frame);
    };
  }, [from, to, climbing]);

  return reading;
}
